import { useMemo, useState } from 'react';
import { Clock, CreditCard, Download, FileSpreadsheet, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExcelImportWizard, ImportKindConfig, ImportResult } from '@/components/ExcelImportWizard';
import { gymApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { exportToExcel } from '@/lib/export-utils';
import {
  CLIENT_FIELDS,
  MEMBERSHIP_FIELDS,
  buildPayload,
  parseRows,
} from '@/lib/import-utils';
import { Link } from 'react-router-dom';

type ExcelKind = 'clients' | 'memberships';

interface ExcelToolsActionsProps {
  kind: ExcelKind;
  exportRows: Record<string, any>[];
  exportFileName: string;
  disabled?: boolean;
  importDisabled?: boolean;
  exportDisabled?: boolean;
  onImported?: () => void;
}

export function ExcelToolsActions({
  kind,
  exportRows,
  exportFileName,
  disabled,
  importDisabled,
  exportDisabled,
  onImported,
}: ExcelToolsActionsProps) {
  const { gym } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const config = useMemo<ImportKindConfig>(() => {
    if (kind === 'memberships') {
      return {
        key: 'memberships',
        label: 'Membresías',
        description: 'Teléfono, plan, fechas y precio de cada clienta.',
        icon: CreditCard,
        fields: MEMBERSHIP_FIELDS,
        templateKind: 'memberships',
        parse: (rows, mapping, rawTypedRows) =>
          parseRows(rows, mapping, MEMBERSHIP_FIELDS, 'memberships', rawTypedRows),
        buildPayload: (parsed) => buildPayload(parsed, 'memberships'),
        importNote: 'Cada fila crea una membresía para la clienta con ese teléfono.',
      };
    }

    return {
      key: 'clients',
      label: 'Clientas',
      description: 'Nombres, teléfono, email, edad, sexo y estado.',
      icon: Users,
      fields: CLIENT_FIELDS,
      supportsMode: true,
      templateKind: 'clients',
      parse: (rows, mapping, rawTypedRows) =>
        parseRows(rows, mapping, CLIENT_FIELDS, 'clients', rawTypedRows),
      buildPayload: (parsed) => buildPayload(parsed, 'clients'),
      importNote:
        'Si una clienta ya existe (mismo teléfono o email) puedes omitirla o actualizarla.',
    };
  }, [kind]);

  const handleImport = async (
    _kind: string,
    rows: Record<string, any>[],
    mode: 'create' | 'upsert'
  ): Promise<ImportResult | null> => {
    if (!gym?.gym_id) return null;

    const res =
      kind === 'clients'
        ? await gymApi.importClientsBatch(gym.gym_id, rows, mode)
        : await gymApi.importMembershipsBatch(gym.gym_id, rows);

    if (!res.success) {
      toast({
        title: 'Error al importar',
        description: res.error || 'No se pudo completar la importación.',
        variant: 'destructive',
      });
      return null;
    }

    return {
      inserted: Number(res.inserted || 0),
      updated: Number(res.updated || 0),
      skipped: Number(res.skipped || 0),
      errors: Array.isArray(res.errors) ? res.errors : [],
    };
  };

  const handleExport = async () => {
    if (!exportRows.length) {
      toast({
        title: 'Nada para exportar',
        description: 'No hay filas que coincidan con la búsqueda o los filtros actuales.',
      });
      return;
    }

    setIsExporting(true);
    try {
      await exportToExcel([{ sheetName: config.label, data: exportRows }], exportFileName);
      const missingPhones =
        kind === 'memberships' ? exportRows.filter((row) => !row.phone).length : 0;
      toast({
        title: 'Exportación lista',
        description:
          `${exportRows.length} fila(s) exportadas a Excel.` +
          (missingPhones
            ? ` ${missingPhones} fila(s) sin teléfono no podrán reimportarse.`
            : ''),
      });
    } catch (error: any) {
      toast({
        title: 'No se pudo exportar',
        description: error?.message || 'Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const notice =
    kind === 'memberships' ? (
      <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-muted-foreground">
          Las membresías se vinculan por teléfono y por el nombre del plan, así que primero debes
          tener registradas a las clientas y creados los planes. Si aún no lo hiciste,{' '}
          <Link to="/clients" className="font-medium text-primary underline underline-offset-2">
            importa las clientas desde Clientes
          </Link>
          .
        </p>
      </div>
    ) : undefined;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        disabled={disabled || importDisabled || !gym}
        className="px-3 md:px-4"
      >
        <FileSpreadsheet className="h-4 w-4 md:mr-2" />
        <span className="hidden md:inline">Importar</span>
      </Button>
      <Button
        variant="outline"
        onClick={handleExport}
        disabled={disabled || exportDisabled || isExporting}
        className="px-3 md:px-4"
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 animate-spin md:mr-2" />
        ) : (
          <Download className="h-4 w-4 md:mr-2" />
        )}
        <span className="hidden md:inline">Exportar</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Importar desde Excel</DialogTitle>
            <DialogDescription>
              Sube tu archivo, revisa el mapeo de columnas y confirma la importación.
            </DialogDescription>
          </DialogHeader>
          <ExcelImportWizard
            kinds={[config]}
            defaultKind={kind}
            title={kind === 'clients' ? 'Importar clientas desde Excel' : 'Importar membresías desde Excel'}
            description={
              kind === 'clients'
                ? 'Migra tu base actual de clientas sin cargar todo a mano.'
                : 'Carga membresías masivamente vinculándolas por teléfono.'
            }
            notice={notice}
            onImport={handleImport}
            onComplete={() => onImported?.()}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
